import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { ActivityPoint } from '@/features/sessions/hooks';
import { ChartTooltip } from './ChartTooltip';
import { useChartTheme } from './useChartTheme';

export interface ActivityChartProps {
  data: readonly ActivityPoint[];
  metric: 'sessions' | 'minutes';
  height?: number;
}

/**
 * Practice over time — a single series, so no legend box: the card title
 * names it. Change over time with a zero baseline makes an area chart the
 * right form; the fill is kept faint so the line, not the wash, carries the
 * reading.
 */
export function ActivityChart({ data, metric, height = 200 }: ActivityChartProps) {
  const theme = useChartTheme();
  const color = theme.series[0] ?? theme.signal;
  const gradientId = `ela-activity-${metric}`;

  // A sparse axis: every label on a 90-day range would collide.
  const tickInterval = Math.max(0, Math.ceil(data.length / 8) - 1);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data as ActivityPoint[]} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>

        <CartesianGrid stroke={theme.grid} vertical={false} />

        <XAxis
          dataKey="label"
          interval={tickInterval}
          tickLine={false}
          axisLine={false}
          tick={{ fill: theme.textMuted, fontSize: 11 }}
          minTickGap={8}
        />
        <YAxis
          allowDecimals={false}
          width={46}
          tickLine={false}
          axisLine={false}
          tick={{ fill: theme.textMuted, fontSize: 11 }}
        />

        <Tooltip
          cursor={{ stroke: theme.axis, strokeWidth: 1, strokeDasharray: '3 3' }}
          content={
            <ChartTooltip
              nameOf={() => (metric === 'sessions' ? 'Sessions' : 'Minutes')}
              format={(value) => String(value)}
            />
          }
        />

        <Area
          // Linear, not monotone: daily counts are discrete observations, and a
          // smoothed spline would draw values between days that never existed.
          type="linear"
          dataKey={metric}
          stroke={color}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          activeDot={{ r: 4, strokeWidth: 2, stroke: theme.surface }}
          dot={false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
